import React, { useEffect, useRef, useState } from "react";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { Alert, Box, Button } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useAuthContext } from "../context/AuthContext";
import { useTranslation } from "../context/Internationalization";
import { isExplicitlyImpersonating } from "../auth/emailChange";
import { remBgImageMimeTypesDropzone } from "../utility/Enum";
import { validateImageFile } from "./Form/validators";
import MultipleFilesUpload from "./Form/MultipleFilesUpload";
import { REM_BG_IMAGE_MAX_SIZE } from "../api/remBgUpload";
import {
  deleteAuthenticatedUserPhoto,
  getUserPhotoErrorTranslationKey,
  uploadAuthenticatedUserPhoto,
} from "../auth/userPhoto";
import { Modal, ModalActions } from "./Modal";
import ModalBody from "./Modal/ModalBody";
import ModalTitle from "./Modal/ModalTitle";

const validationTranslationKeys = {
  empty: "user_photo_empty",
  tooLarge: "user_photo_too_large",
  unsupported: "user_photo_unsupported",
};

const UserPhotoModal = ({ open, onClose, axiosGraphQL, onCompleted }) => {
  const { t } = useTranslation();
  const { authState } = useAuthContext();
  const user = authState?.user;
  const inFlightRef = useRef(false);
  const syncPendingRef = useRef(false);
  const [images, setImages] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [syncPending, setSyncPending] = useState(false);

  useEffect(() => {
    if (!open || inFlightRef.current) return;
    if (syncPendingRef.current) {
      setStatus("idle");
      setError(t("user_photo_refresh_failed"));
      return;
    }
    setImages(user?.photo ? [user.photo] : []);
    setStatus("idle");
    setError("");
    setSyncPending(false);
    inFlightRef.current = false;
  }, [open, user?.photo, t]);

  const pending = status === "pending";
  const impersonating = isExplicitlyImpersonating();
  const selectedImage = images[0];
  const replacement = selectedImage && typeof selectedImage !== "string"
    ? selectedImage
    : null;
  const removal = Boolean(user?.photo) && images.length === 0;
  const hasChange = Boolean(replacement || removal);

  const close = () => {
    if (inFlightRef.current) return;
    if (syncPendingRef.current) {
      onClose();
      return;
    }
    setImages([]);
    setStatus("idle");
    setError("");
    setSyncPending(false);
    syncPendingRef.current = false;
    onClose();
  };

  const refreshAndClose = async () => {
    await onCompleted();
    syncPendingRef.current = false;
    setSyncPending(false);
    onClose();
  };

  const submit = async (event) => {
    event.preventDefault();
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      if (isExplicitlyImpersonating()) {
        setError(t("user_photo_impersonation_forbidden"));
        return;
      }

      const authenticatedUserId = user?._id;
      if (!authenticatedUserId) {
        setError(t("user_photo_authentication_required"));
        return;
      }

      setStatus("pending");
      setError("");

      if (syncPending) {
        try {
          await refreshAndClose();
        } catch (_) {
          setError(t("user_photo_refresh_failed"));
        }
        return;
      }

      if (replacement) {
        const validationError = validateImageFile(
          replacement,
          remBgImageMimeTypesDropzone,
          REM_BG_IMAGE_MAX_SIZE
        );
        if (validationError) {
          setError(t(validationTranslationKeys[validationError]));
          return;
        }
        try {
          await uploadAuthenticatedUserPhoto({
            authenticatedUserId,
            file: replacement,
          });
        } catch (uploadError) {
          setError(t(getUserPhotoErrorTranslationKey(uploadError)));
          return;
        }
      } else if (removal) {
        try {
          await deleteAuthenticatedUserPhoto({
            authenticatedUserId,
            currentPhoto: user.photo,
            axiosGraphQL,
          });
        } catch (removeError) {
          if (removeError?.statusCode === 409) {
            try {
              await onCompleted();
              setError(t("user_photo_conflict"));
            } catch (_) {
              syncPendingRef.current = true;
              setSyncPending(true);
              setError(t("user_photo_refresh_failed"));
            }
            return;
          }
          setError(t("user_photo_remove_failed"));
          return;
        }
      } else {
        return;
      }

      syncPendingRef.current = true;
      setSyncPending(true);
      try {
        await refreshAndClose();
      } catch (_) {
        setError(t("user_photo_refresh_failed"));
      }
    } finally {
      setStatus("idle");
      inFlightRef.current = false;
    }
  };

  const title = t(user?.photo ? "edit_user_photo" : "add_user_photo");

  return (
    <Modal
      open={open}
      onClose={close}
      type="info"
      ariaLabel={title}
      closeLabel={t("close")}
    >
      <form onSubmit={submit}>
        <ModalTitle title={title} />
        <ModalBody>
          <Box sx={{ width: "100%" }}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {impersonating ? (
              <Alert severity="info">
                {t("user_photo_impersonation_forbidden")}
              </Alert>
            ) : (
              <Box
                sx={{
                  pointerEvents: pending || syncPending ? "none" : "auto",
                  opacity: pending || syncPending ? 0.6 : 1,
                }}
              >
                <MultipleFilesUpload
                  label={t("user_photo")}
                  name="userPhoto"
                  value={images}
                  onChange={(event) => {
                    if (inFlightRef.current || syncPending) return;
                    setImages(event.target.value);
                    setError("");
                  }}
                  acceptedFileTypes={remBgImageMimeTypesDropzone}
                  singleFileUpload
                  maxSize={REM_BG_IMAGE_MAX_SIZE}
                  maxSizeError={t("user_photo_too_large")}
                  requirementsText={t("user_photo_upload_requirements")}
                />
              </Box>
            )}
          </Box>
        </ModalBody>
        <ModalActions>
          <Button
            variant="outlined"
            color="primary"
            fullWidth
            onClick={close}
            disabled={pending}
          >
            {t("cancel")}
          </Button>
          {!impersonating && (
            <LoadingButton
              startIcon={
                syncPending ? (
                  <RefreshOutlinedIcon />
                ) : removal ? (
                  <DeleteOutlineIcon />
                ) : (
                  <CheckOutlinedIcon />
                )
              }
              loadingPosition="start"
              type="submit"
              fullWidth
              loading={pending}
              disabled={!syncPending && !hasChange}
            >
              {syncPending
                ? t("retry_user_photo_refresh")
                : removal
                ? t("remove_user_photo")
                : t("save")}
            </LoadingButton>
          )}
        </ModalActions>
      </form>
    </Modal>
  );
};

export default UserPhotoModal;
